import {
  circleArcsWhere,
  circleHitsAabb,
  circleHitsPolygonBounds,
  clipPolygonToDisk,
  diskSides,
  triangulatePolygon,
  type AngleArc,
} from './geometry.ts';
import { resolveElevationStack } from './map/elevationStack.ts';
import { pointUnderPlateau, visualColumnBounds } from './map/extrude.ts';
import { pointInPolygon } from './map/islands.ts';
import { tessellateRing } from './map/ring.ts';
import type { MapDocument } from './map/types.ts';
import type { Vec2 } from './types.ts';

export type { AngleArc } from './geometry.ts';
export { complementArcs } from './geometry.ts';

export type CircleRimClasses = {
  land: AngleArc[];
  underCap: AngleArc[];
};

type CapProbe = {
  footprint: readonly Vec2[];
  elevation: number;
};

function surfaceOutlines(map: MapDocument): Vec2[][] {
  return [...map.islands, ...map.rocks].map((ring) => tessellateRing(ring));
}

/** Disk ∩ islands/rocks. Shallows and the void are not surface obstacles. */
export function circleSurfaceOverlap(
  map: MapDocument,
  center: Vec2,
  radius: number,
): Vec2[][] {
  if (radius <= 0) {
    return [];
  }

  const sides = diskSides(radius);
  const regions: Vec2[][] = [];

  for (const outline of surfaceOutlines(map)) {
    if (!circleHitsPolygonBounds(center, radius, outline)) {
      continue;
    }

    const clipped = clipPolygonToDisk(outline, center, radius, sides);

    if (clipped.length >= 3) {
      regions.push(clipped);
    }
  }

  return regions;
}

/** Same regions as `circleSurfaceOverlap`, already ear-clipped for the renderer. */
export function circleSurfaceOverlapTriangles(
  map: MapDocument,
  center: Vec2,
  radius: number,
): [Vec2, Vec2, Vec2][] {
  const triangles: [Vec2, Vec2, Vec2][] = [];

  for (const region of circleSurfaceOverlap(map, center, radius)) {
    triangles.push(...triangulatePolygon(region));
  }

  return triangles;
}

function prepareRimBodies(
  map: MapDocument,
  center: Vec2,
  radius: number,
): { landOutlines: Vec2[][]; caps: CapProbe[] } {
  const stack = resolveElevationStack(map);
  const landOutlines: Vec2[][] = [];
  const caps: CapProbe[] = [];

  for (const body of [...map.islands, ...map.rocks]) {
    const footprint = tessellateRing(body);

    if (circleHitsPolygonBounds(center, radius, footprint)) {
      landOutlines.push(footprint);
    }

    const elevation = stack.get(body.id)?.worldElevation ?? body.elevation;
    const box = visualColumnBounds(footprint, elevation);

    if (box && circleHitsAabb(center, radius, box)) {
      caps.push({ footprint, elevation });
    }
  }

  return { landOutlines, caps };
}

function pointOnLand(point: Vec2, outlines: readonly Vec2[][]): boolean {
  return outlines.some((outline) => pointInPolygon(point, outline));
}

function pointOnUnderCap(
  point: Vec2,
  outlines: readonly Vec2[][],
  caps: readonly CapProbe[],
): boolean {
  if (pointOnLand(point, outlines)) {
    return false;
  }

  return caps.some((cap) => pointUnderPlateau(cap.footprint, cap.elevation, point));
}

/**
 * Circumference runs that sit on an island or rock.
 *
 * The predicate runs at least 128 times per call, plus a binary refinement at every land/water
 * boundary, and this is called for the planning circle and the prediction circle on every hover.
 * It therefore takes its outlines from a single up-front pass and narrows them to the ones the
 * disk can actually reach, rather than asking the map per sample.
 */
export function circleRimOnSurface(
  map: MapDocument,
  center: Vec2,
  radius: number,
): AngleArc[] {
  return circleRimClasses(map, center, radius).land;
}

/**
 * Waterline land arcs plus legal water hidden by a north cap.
 *
 * One outline / extruded-AABB prune, then two 128-sample walks on those lists — not a
 * `pointUnderColumnCap` walk of every body per sample. `land` matches the old land-only rim.
 */
export function circleRimClasses(
  map: MapDocument,
  center: Vec2,
  radius: number,
): CircleRimClasses {
  if (radius <= 0) {
    return { land: [], underCap: [] };
  }

  const { landOutlines, caps } = prepareRimBodies(map, center, radius);

  return {
    land:
      landOutlines.length === 0
        ? []
        : circleArcsWhere(center, radius, (point) => pointOnLand(point, landOutlines)),
    underCap:
      caps.length === 0
        ? []
        : circleArcsWhere(center, radius, (point) =>
            pointOnUnderCap(point, landOutlines, caps),
          ),
  };
}

export type OutlineRimRuns = {
  water: Vec2[][];
  underCap: Vec2[][];
};

type RimKind = 'water' | 'land' | 'underCap';

function rimKindAt(
  point: Vec2,
  landOutlines: readonly Vec2[][],
  caps: readonly CapProbe[],
): RimKind {
  if (pointOnLand(point, landOutlines)) {
    return 'land';
  }

  if (pointOnUnderCap(point, landOutlines, caps)) {
    return 'underCap';
  }

  return 'water';
}

function cutRimEdge(
  from: Vec2,
  to: Vec2,
  fromKind: RimKind,
  kindAt: (point: Vec2) => RimKind,
): Vec2 {
  let water = 0;
  let land = 1;

  for (let step = 0; step < 8; step += 1) {
    const mid = (water + land) / 2;
    const point = {
      x: from.x + (to.x - from.x) * mid,
      y: from.y + (to.y - from.y) * mid,
    };

    if (kindAt(point) === fromKind) {
      water = mid;
    } else {
      land = mid;
    }
  }

  return {
    x: from.x + (to.x - from.x) * water,
    y: from.y + (to.y - from.y) * water,
  };
}

function mergeClosedRuns(runs: Vec2[][]): Vec2[][] {
  if (runs.length < 2) {
    return runs;
  }

  const first = runs[0];
  const last = runs[runs.length - 1];
  const start = first?.[0];
  const end = last?.[last.length - 1];

  if (!first || !last || !start || !end || start.x !== end.x || start.y !== end.y) {
    return runs;
  }

  return [[...last.slice(0, -1), ...first], ...runs.slice(1, -1)];
}

/**
 * Open-water and under-cap runs along a closed outline. Land is omitted, the same
 * way the planning disk drops land arcs. `radius` is the prune distance around `center`.
 */
export function classifyOutline(
  map: MapDocument,
  points: readonly Vec2[],
  center: Vec2,
  radius: number,
): OutlineRimRuns {
  if (points.length < 2 || radius <= 0) {
    return { water: [], underCap: [] };
  }

  const { landOutlines, caps } = prepareRimBodies(map, center, radius);
  const kindAt = (point: Vec2): RimKind => rimKindAt(point, landOutlines, caps);
  const water: Vec2[][] = [];
  const underCap: Vec2[][] = [];
  const sink = (kind: RimKind): Vec2[][] | null => {
    if (kind === 'water') {
      return water;
    }

    if (kind === 'underCap') {
      return underCap;
    }

    return null;
  };

  let kind = kindAt(points[0]!);
  let run: Vec2[] = [points[0]!];

  for (let index = 1; index < points.length; index += 1) {
    const next = points[index]!;
    const nextKind = kindAt(next);

    if (nextKind === kind) {
      run.push(next);
      continue;
    }

    const cut = cutRimEdge(run[run.length - 1]!, next, kind, kindAt);
    run.push(cut);
    sink(kind)?.push(run);
    kind = nextKind;
    run = [cut, next];
  }

  sink(kind)?.push(run);

  return {
    water: mergeClosedRuns(water),
    underCap: mergeClosedRuns(underCap),
  };
}
